import React, { useState, useEffect, Fragment, useRef } from 'react';
import Button from '@mui/material/Button';
import FieldNames from '../../defaults/FieldNames.json';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { emitCustomEvent } from 'react-custom-events';
import Events from '../../defaults/Events';
import PropTypes from 'prop-types';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ButtonGroup, Popper, Grow, Paper, MenuItem, MenuList, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import './DetailsHeader.css';
import DetailsRequired from './DetailsRequired';
import DetailsEdited from './DetailsEdited';
import { useLocation } from 'react-router';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import {useSelector} from "react-redux";


export default function DetailsHeader({ feature, getAllMissingFieldsCount, getAllUnsavedFieldsCount, closeDetail,
  onEditClicked, onCopyClicked, onCopyAsClicked, onDeleteClicked, onSaveChanges, mode, isNew, chipClicked, chipClickedUnsaved,
  isTreatment, hasActual, isApproved, isSaveDisabled, setIsSaveDisabled, disableEdit, disableEditMessage, disableDelete, userInfo })
{
  const [label, setLabel] = useState("");
  const [cancelDetailDialogOpen, setCancelDetailDialogOpen] = useState(false);
  const [openDeleteFeatureDialog, setOpenDeleteFeatureDialog] = useState(false);
  const [missingFields, setMissingFields] = useState(0);
  const [unsavedFields, setUnsavedFields] = useState(0);
  const [actionOpen, setActionOpen] = useState(false);
  const [actualsActionOpen, setActualsActionOpen] = useState(false);
  const anchorRef = useRef(null);
  const anchorRefActuals = useRef(null);
  const buttonStyle = { marginLeft: '10px', display: 'inline', height: '30px' }
  const [ isActuals, setIsActuals ] = useState(false);
  const location = useLocation();
  const [isArchived, setIsArchived] = useState('No');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPoint, setIsPoint] = useState(false);
  const treatmentValidationErrors = useSelector((state) => state.treatmentValidation?.ValidationErrorFields);
  const actualTreatmentValidationErrors = useSelector((state) => state.actualsTreatmentValidation?.ValidationErrorFields);
  const activityValidationErrors = useSelector((state) => state.activityValidation?.ValidationErrorFields);
  const actualActivityValidationErrors = useSelector((state) => state.actualsActivityValidation?.ValidationErrorFields);

  useEffect(() => {
    getValidationErrors();
  }, [treatmentValidationErrors, actualTreatmentValidationErrors, activityValidationErrors, actualActivityValidationErrors, isTreatment, isActuals]);

  const handleActionToggle = () => {
    setActionOpen((prevOpen) => !prevOpen);
  };

  const handleActionClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setActionOpen(false);
  };

  const getValidationErrors = () => {
    let errors;
    if (isTreatment){
      if(isActuals){
        errors = actualTreatmentValidationErrors?.length > 0 || false;
      } else {
        errors = treatmentValidationErrors?.length > 0 || false;
      }
    } else {
      if(isActuals){
        errors = actualActivityValidationErrors?.length > 0 || false;
      } else {
        errors= activityValidationErrors?.length > 0 || false;
      }
    }
    setIsSaveDisabled(errors);
  }

  const handleActualsActionToggle = () => {
    setActualsActionOpen((prevOpen) => !prevOpen);
  };

  const handleActualsActionClose = (event) => {
    if(anchorRefActuals.current && anchorRefActuals.current.contains(event.target)) {
      return;
    }
    setActualsActionOpen(false);
  }

  useEffect(() => {
    if(mode === FieldNames.modes.edit){
      setMissingFields(getAllMissingFieldsCount(feature, isTreatment, userInfo, isActuals, null));
      setUnsavedFields(getAllUnsavedFieldsCount(feature));
    }
  }, [getAllMissingFieldsCount, getAllUnsavedFieldsCount, feature, isTreatment, mode, userInfo, isActuals])

  useEffect(() =>
  {
    let updatedLabel = "";
    if (isNew && (!feature || !feature.attributes || !feature.attributes[FieldNames.featureFields.name]))
    {
      if(isTreatment){
        updatedLabel = "New Treatment";
      } else {
        updatedLabel = "New Activity";
      }
    }
    else
    {
      updatedLabel = (feature && feature.attributes) ? feature.attributes[FieldNames.featureFields.name] : "";
    }
    setLabel(updatedLabel);
    if(feature && feature.attributes){
      if(!isTreatment && feature.attributes[FieldNames.featureFields.isPoint] === FieldNames.yesNo.yes){
        setIsPoint(true);
      } else {
        setIsPoint(false);
      }
      setIsArchived(feature.attributes[FieldNames.featureFields.isArchived]);
      if(isActuals && feature.attributes[FieldNames.featureFields.completionDate]){
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
      //Query table to see if this has an actuals
    }
  }, [feature, isNew, isTreatment, isActuals]);

  const onEdit = () => {
    onEditClicked();
  }

  const onCopy = async () => {
    await onCopyClicked();
    setActionOpen(false);
  }

  const onCopyAs = async () => {
    await onCopyAsClicked(isTreatment);
    setActionOpen(false);
  }

  const onDelete = () => {
    if(isActuals){
      setActualsActionOpen(false);
    } else {
      setActionOpen(false);
    }
    setOpenDeleteFeatureDialog(true);
  }

  const onClose = () => {
    if (isNew)
    {
      setCancelDetailDialogOpen(true);
      return;
    }
    closeDetail(null);
    emitCustomEvent(Events.ClearModifiedLayer);
  }

  const handleAgreeToAbandonFeature = () =>
  {
    const geoType = feature?.attributes[FieldNames.featureFields.joinFeature];
    const isPostFire = isFeaturePostFire(feature?.attributes)
    const isActual = (feature?.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity || feature?.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment) ? true : false;
    setCancelDetailDialogOpen(false);
    closeDetail(null);
    const featureClass = isActual ? (isTreatment ? FieldNames.classTypes.actualTreatment : FieldNames.classTypes.actualActivity) : (isTreatment ? FieldNames.classTypes.treatment : FieldNames.classTypes.activity);
    emitCustomEvent(Events.ClearGraphicsEvent, { geometryType: geoType, isPostFire: isPostFire, featureClass: featureClass });
    emitCustomEvent(Events.ClearModifiedLayer);
  }

  const handleAgreeToArchiveFeature = async () => {
    const endResult = await onDeleteClicked();
    setOpenDeleteFeatureDialog(false);
  }

  const closeCancelDetailDialog = () =>
  {
    setCancelDetailDialogOpen(false);
  }

  const closeDeleteFeatureDialog = () => {
    setOpenDeleteFeatureDialog(false);
  }

  const anyMissingFields = () => {
    if(missingFields > 0 && mode === FieldNames.modes.edit) {
      return (
        <DetailsRequired chipClicked={chipClicked} missingFields={missingFields} />
      )
    } else {
      return (
        <Fragment></Fragment>
      )
    }
  }

  const anyUnsavedFields = () => {
    if(unsavedFields > 0 && mode === FieldNames.modes.edit) {
      return (
        <DetailsEdited chipClicked={chipClickedUnsaved} unsavedFields={unsavedFields} />
      )
    } else {
      return (
        <Fragment></Fragment>
      )
    }
  }

  return (
    <div className="incident-details-header">
      <div className='incident-details-top'>
        {isArchived === FieldNames.yesNo.yes &&
          <h3 style={{color: 'red', whiteSpace: 'pre-wrap'}}>{label} - Archived</h3>
        }
        {isArchived !== FieldNames.yesNo.yes &&
          <h3 style={{whiteSpace: 'pre-wrap'}}>{label}</h3>
        }

        <div className="incident-details-header-actions">
        {(mode === FieldNames.modes.read && isArchived !== FieldNames.yesNo.yes) &&
          <Tooltip title={disableEdit ? disableEditMessage : ""} arrow>
            <span>
              <Button
              onClick={onEdit}
              color="primary"
              variant="outlined"
              style={buttonStyle}
              size="small"
              disabled={disableEdit}
              >
                Edit
              </Button>
            </span>
          </Tooltip>
          }

          {mode === FieldNames.modes.edit &&
            <Button
            onClick={onSaveChanges}
            color="primary"
            style={buttonStyle}
            disabled={isSaveDisabled}
            variant="outlined"
            size="small">Save</Button>
          }
          <Button
            onClick={onClose}
            variant="outlined"
            style={buttonStyle}
            size="small">Close</Button>

          {(isActuals && !isCompleted && !isNew) &&
            <Fragment>
              <ButtonGroup variant="outlined" ref={anchorRefActuals} aria-label="split button" style={buttonStyle}>
                <Button color="primary" size="small" style={{ height: '30px' }}>Actions</Button>
                <Button
                  size="small"
                  color="primary"
                  aria-controls={actualsActionOpen ? 'split-button-menu' : undefined}
                  aria-expanded={actualsActionOpen ? 'true' : undefined}
                  aria-label="actionsDrowdown"
                  aria-haspopup="menu"
                  style={{ height: '30px', minWidth: '20px', width: '30px'}}
                  onClick={handleActualsActionToggle}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>

              <Popper
                open={actualsActionOpen}
                anchorEl={anchorRefActuals.current}
                role={undefined}
                transition
                disablePortal
                style={{ maxWidth: '300px', zIndex: 5}}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleActualsActionClose}>
                        <div>
                          <MenuList id="split-button-menu" autoFocusItem>
                            {isArchived !== FieldNames.yesNo.yes && !disableDelete &&
                              <MenuItem onClick={() => onDelete()}>
                                Delete
                              </MenuItem>
                            }
                          </MenuList>
                        </div>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Fragment>
          }

          {!isActuals &&
            <Fragment>
              <ButtonGroup variant="outlined" ref={anchorRef} aria-label="split button" style={buttonStyle}>
                <Button color="primary" size="small" style={{ height: '30px' }}>Actions</Button>
                <Button
                  size="small"
                  color="primary"
                  aria-controls={actionOpen ? 'split-button-menu' : undefined}
                  aria-expanded={actionOpen ? 'true' : undefined}
                  aria-label="actionsDrowdown"
                  aria-haspopup="menu"
                  style={{ height: '30px', minWidth: '20px', width: '30px'}}
                  onClick={handleActionToggle}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>

              <Popper
                open={actionOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                style={{ maxWidth: '300px', zIndex: 5}}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleActionClose}>
                        <div>
                          <MenuList id="split-button-menu" autoFocusItem>
                            {!isPoint &&
                              <div>
                                <MenuItem onClick={async() => await onCopy()}>
                                  {isTreatment ? 'Copy As Treatment' : 'Copy As Activity'}
                                </MenuItem>
                                {/*Postfire activities do not allow polylines, so prevent copying postfire polyline treatments to activities*/}
                                {!(feature?.attributes?.Class === 'Estimated Treatment' && feature?.geometry?.type === 'polyline') &&
                                  <MenuItem onClick={async() => await onCopyAs()}>
                                    {isTreatment ? 'Copy As Activity' : 'Copy As Treatment'}
                                  </MenuItem>
                                }
                              </div>
                            }

                            {isPoint &&
                              <div>
                                <MenuItem onClick={async() => await onCopy()}>
                                  {'Copy As Activity'}
                                </MenuItem>
                              </div>
                            }

                            {(isArchived !== FieldNames.yesNo.yes && !hasActual && !isApproved) &&
                              <MenuItem onClick={() => onDelete()}>
                                Delete
                              </MenuItem>
                            }
                          </MenuList>
                        </div>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Fragment>
          }
        </div>
      </div>
      <div className='incident-details-bottom'>
        {anyUnsavedFields()}
        {anyMissingFields()}
        <Dialog
          open={cancelDetailDialogOpen}
          onClose={closeCancelDetailDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">
            {!isTreatment ? "Cancel New Activity" : "Cancel New Treatment?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {!isTreatment ? "This activity has not been saved, are you sure you want to discard it?" : "This treatment has not been saved, are you sure you want to discard it?"}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAgreeToAbandonFeature}>Yes</Button>
            <Button onClick={closeCancelDetailDialog} autoFocus>
              No
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteFeatureDialog}
          onClose={closeDeleteFeatureDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          {isActuals &&
            <DialogTitle id="alert-dialog-title">
              {!isTreatment ? "Delete Actual Activity" : "Delete Actual Treatment?"}
            </DialogTitle>
          }
          {!isActuals &&
            <DialogTitle id="alert-dialog-title">
              {!isTreatment ? "Delete Activity" : "Delete Treatment?"}
            </DialogTitle>
          }
          <DialogContent>
            {isActuals &&
              <DialogContentText id="alert-dialog-description">
                {!isTreatment ? "This actual activity will be removed, are you sure you want to delete it?" : "This actual treatment will be removed, are you sure you want to delete it?"}
              </DialogContentText>
            }
            {!isActuals &&
              <DialogContentText id="alert-dialog-description">
                {!isTreatment ? "This activity will be removed, are you sure you want to delete it?" : "This treatment will be removed, are you sure you want to delete it?"}
              </DialogContentText>
            }
          </DialogContent>
          <DialogActions>
            <Button onClick={async() => { await handleAgreeToArchiveFeature() }}>Yes</Button>
            <Button onClick={closeDeleteFeatureDialog} autoFocus>
              No
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
